/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { dsAppUidProp } from 'componentregistry/components/DataStore/DataStoreManager';
import { importFileType } from 'componentregistry/registry/constants';
import { isEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionImport';

const ActionImportManager: ActionManagerFn<'ActionImport'> = (
  platform: Platform,
): ActionManager<'ActionImport'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionImport'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      gridItemId: {
        label: 'Grid Item ID',
        type: 'ItemID',
      },
      fileType: {
        label: 'File Type',
        options: importFileType,
        type: 'select',
      },
      hideInsertMode: {
        label: 'Hide Insert Mode',
        type: 'boolean',
      },
      hideUpdateMode: {
        label: 'Hide Update Mode',
        type: 'boolean',
      },
      mergeMode: {
        label: ' Merge Mode',
        type: 'boolean',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'hideInsertMode':
          return !props.mergeMode && !props.hideUpdateMode;
        case 'hideUpdateMode':
          return !props.mergeMode && !props.hideInsertMode;
        case 'mergeMode':
          return !props.hideInsertMode && !props.hideUpdateMode;
        case 'dataSource':
          return isEmpty(props.gridItemId);
        case 'gridItemId':
          return isEmpty(props.dataSource);
        default:
          return true;
      }
    },
    name: 'Import Data',
    icon: { iconName: 'file-import' },
    getDisplayText: (item) => item.props.dataSource,
    description: `**Overview**<br/>
The Import Data action is a data integration component designed to ingest external data from files (CSV or XLSX) into the application's database. It creates a bridge between offline files and the system's Data Sources, typically launching a user interface wizard that allows users to upload files, map columns to database fields, and define how the data should be synchronized.<br/><br/>
This component offers flexible configuration for the import strategy. Developers can control whether users are allowed to insert new records, update existing ones, or perform a merge operation. It also allows for post-processing logic to be executed immediately after the import finishes, utilizing the data context provided by the operation.

**Key Features**<br/>
* **Dual Targeting:** Can be configured to target either a specific DataSource directly or a Grid component (gridItemId), allowing for context-aware imports depending on the UI setup.
* **Operation Control:** Features specific boolean flags (hideInsertMode, hideUpdateMode, mergeMode) to restrict the import wizard's capabilities (e.g., forcing an "Update Only" workflow by hiding the insert option).
* **Response Handling:** Explicitly allows ActionOnResponse as a child component, enabling developers to chain actions that run only after the import successfully completes.
* **Context Data Access:** Exposes import details via the $._.response object (containing fileName, count, uploadMode), which can be used in messages or logs.

**Properties**<br/>
* **Data Source:** A DataSourceChooser property identifying the backend target.
  * *Visibility:* Only visible if gridItemId is empty.
* **Grid Item ID:** An ItemID property identifying a UI grid target.
  * *Visibility:* Only visible if dataSource is empty.
* **File Type:** A select property to specify allowed formats (e.g., CSV, XLSX).
* **Hide Insert Mode:** A boolean to remove the "Insert" option from the import dialog.
* **Hide Update Mode:** A boolean to remove the "Update" option from the import dialog.
* **Merge Mode:** A boolean to enable the "Merge" strategy (insert new, update existing) in the import dialog.

**Use Cases**<br/>
* **Bulk Data Entry:** Allowing an administrator to upload a CSV file containing hundreds of new user accounts to create them in one batch.
* **Price Updates:** Uploading an Excel sheet containing Product IDs and new Prices, configured with hideInsertMode to ensure the system only updates existing products and doesn't accidentally create duplicates.

\`\`\`javascript
    // The info related to import is available as $._.response
    // Sample expression using the above values from the import
    \`\${$._.response.fileName} \${$._.response.count} \${$._.response.uploadMode}!\`
    \`\`\``,
  };
};

export default ActionImportManager;
